import { getServerErrorMessage } from '@airship-platform/http-service';
import { OrganizationUser } from './organizationUsers.models';
import { deriveInvitedUserIds, markInvited } from './utils/inviteUsers.utils';

export interface InviteFailure {
  userId?: string;
  reason: string;
}

export interface OrganizationUsersState {
  ids: string[];
  entities: Record<string, OrganizationUser>;
  currentUserId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  inviteFailures: InviteFailure[];
}

type Action =
  | { type: 'FETCH_USERS' }
  | { type: 'FETCH_USERS_SUCCESS'; payload: { users: OrganizationUser[] } }
  | { type: 'FETCH_USERS_FAILURE'; payload: { error: string } }
  | {
      type: 'INVITE_USERS';
      payload: { userIds: string[] };
    }
  | {
      type: 'INVITE_USERS_SUCCESS';
      payload: { userIds: string[]; failed: InviteFailure[] };
    }
  | { type: 'INVITE_USERS_FAILURE'; payload: { error: unknown } }
  | {
      type: 'CREATE_USER';
    }
  | {
      type: 'CREATE_USER_SUCCESS';
      payload: { userId?: string; failed: InviteFailure[] };
    }
  | { type: 'CREATE_USER_FAILURE'; payload: { error: unknown } }
  | { type: 'FETCH_USER'; payload: { userId: string } }
  | { type: 'FETCH_USER_SUCCESS'; payload: { user: OrganizationUser } }
  | { type: 'FETCH_USER_FAILURE'; payload: { error: string } }
  | { type: 'UPDATE_USER' }
  | { type: 'UPDATE_USER_SUCCESS'; payload: { user: OrganizationUser } }
  | { type: 'UPDATE_USER_FAILURE'; payload: { error: string } }
  | { type: 'CLEAR_ERROR' };

export const initState: OrganizationUsersState = {
  ids: [],
  entities: {},
  currentUserId: null,
  isLoaded: false,
  isLoading: false,
  error: null,
  inviteFailures: [],
};

export const organizationUsersReducer = (
  state: OrganizationUsersState,
  action: Action
): OrganizationUsersState => {
  switch (action.type) {
    case 'FETCH_USERS':
      return {
        ...state,
        isLoading: true,
        isLoaded: !!state.ids.length,
        error: null,
      };
    case 'FETCH_USERS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.users.map((a) => a.userId),
          ]),
        ],
        entities: action.payload.users.reduce(
          (prev, curr) => ({ ...prev, [curr.userId]: curr }),
          state.entities
        ),
      };

    case 'INVITE_USERS':
      return {
        ...state,
        isLoading: true,
        error: null,
        inviteFailures: [],
      };
    case 'INVITE_USERS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        entities: markInvited(
          state.entities,
          deriveInvitedUserIds(action.payload.userIds, action.payload.failed)
        ),
        inviteFailures: action.payload.failed,
      };
    case 'CREATE_USER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        entities: markInvited(
          state.entities,
          deriveInvitedUserIds(
            action.payload.userId ? [action.payload.userId] : [],
            action.payload.failed
          )
        ),
        inviteFailures: action.payload.failed,
      };

    case 'FETCH_USER':
      return {
        ...state,
        currentUserId: action.payload.userId,
        isLoading: true,
        isLoaded: !!state.ids.includes(action.payload.userId),
        error: null,
      };
    case 'FETCH_USER_SUCCESS':
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        ids: [...new Set([...state.ids, action.payload.user.userId])],
        entities: {
          ...state.entities,
          [action.payload.user.userId]: action.payload.user,
        },
      };
    case 'CREATE_USER':
      return { ...state, isLoading: true, error: null, inviteFailures: [] };
    case 'UPDATE_USER':
      return { ...state, isLoading: true, error: null };
    case 'UPDATE_USER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          [action.payload.user.userId]: action.payload.user,
        },
      };
    case 'CREATE_USER_FAILURE':
      return {
        ...state,
        isLoading: false,
        error:
          getServerErrorMessage(action.payload.error) ??
          'Failed to create user',
      };
    case 'INVITE_USERS_FAILURE':
      return {
        ...state,
        isLoading: false,
        error:
          getServerErrorMessage(action.payload.error) ??
          'Failed to invite user',
      };
    case 'UPDATE_USER_FAILURE':
    case 'FETCH_USER_FAILURE':
    case 'FETCH_USERS_FAILURE':
      return { ...state, isLoading: false, error: action.payload.error };
    case 'CLEAR_ERROR':
      return { ...state, error: null, inviteFailures: [] };
    default:
      return state;
  }
};
